/**
 * Scroll Depth Tracking for AIALGO.trading
 * Tracks user engagement through scroll behavior to measure content consumption
 */

interface ScrollTrackingConfig {
  thresholds?: number[] // Percentage thresholds (e.g., [25, 50, 75, 90])
  trackTimeToScroll?: boolean // Track time taken to reach thresholds
  minTimeOnPage?: number // Minimum time (ms) before tracking scroll
}

interface ScrollEvent {
  threshold: number
  timeToScroll: number
  timestamp: number
  page: string
  sessionId: string
}

class ScrollTracker {
  private thresholds: number[]
  private trackedThresholds: Set<number>
  private sessionStartTime: number
  private thresholdTimings: Map<number, number>
  private config: ScrollTrackingConfig
  private sessionId: string
  private observer: IntersectionObserver | null = null

  constructor(config: ScrollTrackingConfig = {}) {
    this.config = {
      thresholds: [25, 50, 75, 90, 100],
      trackTimeToScroll: true,
      minTimeOnPage: 5000, // 5 seconds
      ...config,
    }

    this.thresholds = this.config.thresholds || []
    this.trackedThresholds = new Set()
    this.sessionStartTime = Date.now()
    this.thresholdTimings = new Map()
    this.sessionId = this.getSessionId()

    if (typeof window !== 'undefined') {
      this.initializeTracking()
    }
  }

  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('scroll_session_id')
    if (!sessionId) {
      sessionId = `scroll_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      sessionStorage.setItem('scroll_session_id', sessionId)
    }
    return sessionId
  }

  private initializeTracking(): void {
    // Initial scroll position
    let lastScrollTop = 0
    let ticking = false

    const scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.checkScrollDepth()
          ticking = false
        })
        ticking = true
      }
    }

    // Listen to scroll events
    window.addEventListener('scroll', scrollHandler, { passive: true })

    // Also track visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.flushEvents()
      }
    })

    // Flush events on page unload
    window.addEventListener('beforeunload', () => {
      this.flushEvents()
    })
  }

  private checkScrollDepth(): void {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    const scrollPercent = (scrollTop / docHeight) * 100

    // Check each threshold
    this.thresholds.forEach(threshold => {
      if (scrollPercent >= threshold && !this.trackedThresholds.has(threshold)) {
        const timeElapsed = Date.now() - this.sessionStartTime

        // Check minimum time on page requirement
        if (timeElapsed >= (this.config.minTimeOnPage || 0)) {
          this.trackThreshold(threshold, timeElapsed)
        }
      }
    })
  }

  private trackThreshold(threshold: number, timeElapsed: number): void {
    // Mark threshold as tracked
    this.trackedThresholds.add(threshold)
    this.thresholdTimings.set(threshold, timeElapsed)

    const scrollEvent: ScrollEvent = {
      threshold,
      timeToScroll: timeElapsed,
      timestamp: Date.now(),
      page: window.location.pathname,
      sessionId: this.sessionId,
    }

    // Send to PostHog if available
    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('scroll_depth_reached', {
        scroll_threshold: threshold,
        time_to_scroll_ms: timeElapsed,
        page: window.location.pathname,
        session_id: this.sessionId,
      })
    }

    // Track max scroll depth
    const maxThreshold = Math.max(...this.trackedThresholds)
    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('max_scroll_depth', {
        max_threshold,
        total_time_on_page_ms: timeElapsed,
        page: window.location.pathname,
      })
    }

    console.log(`[Analytics] Scroll depth ${threshold}% reached in ${timeElapsed}ms`)
  }

  private flushEvents(): void {
    if (this.trackedThresholds.size === 0) return

    const maxThreshold = Math.max(...this.trackedThresholds)
    const totalTime = Date.now() - this.sessionStartTime

    // Send final summary
    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('scroll_session_complete', {
        max_scroll_depth: maxThreshold,
        thresholds_reached: Array.from(this.trackedThresholds),
        total_time_on_page_ms: totalTime,
        page: window.location.pathname,
        session_id: this.sessionId,
      })
    }
  }

  /**
   * Get current scroll statistics
   */
  getStats(): {
    maxThreshold: number
    thresholdsReached: number[]
    timeElapsed: number
  } {
    const maxThreshold = this.trackedThresholds.size > 0
      ? Math.max(...this.trackedThresholds)
      : 0

    return {
      maxThreshold,
      thresholdsReached: Array.from(this.trackedThresholds),
      timeElapsed: Date.now() - this.sessionStartTime,
    }
  }

  /**
   * Reset tracking (useful for SPA navigation)
   */
  reset(): void {
    this.trackedThresholds.clear()
    this.thresholdTimings.clear()
    this.sessionStartTime = Date.now()
  }
}

// Singleton instance
let scrollTracker: ScrollTracker | null = null

/**
 * Initialize scroll tracking
 */
export function initScrollTracking(config?: ScrollTrackingConfig): ScrollTracker {
  if (!scrollTracker) {
    scrollTracker = new ScrollTracker(config)
  }
  return scrollTracker
}

/**
 * Get scroll tracker instance
 */
export function getScrollTracker(): ScrollTracker | null {
  return scrollTracker
}

/**
 * Reset scroll tracking (call on route changes)
 */
export function resetScrollTracking(): void {
  if (scrollTracker) {
    scrollTracker.reset()
  }
}
